import {
  Currency,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  SectionItem,
  Tooltip,
} from '@mairistem/composants';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import { PERIODICITE_ARRETE, PERIODICITE_VERSEMENT } from '@/constants/periodicite';
import type { ErrorFieldType } from '@/types';

interface EncaisseArretesProps {
  identifiant: string | number;
  errors?: ErrorFieldType[];
  encaisseMaximum?: number;
  encaisseNumeraireMaximum?: number;
  fondCaisse?: number;
  periodiciteVersementEncaisseGlobale?: string;
  periodiciteArrete?: string;
  dateFinDernierArrete?: string;
  onChange: (e: any) => void;
}

const EncaisseArretes = (props: EncaisseArretesProps) => {
  const {
    identifiant,
    errors,
    encaisseMaximum,
    encaisseNumeraireMaximum,
    fondCaisse,
    periodiciteVersementEncaisseGlobale,
    periodiciteArrete,
    dateFinDernierArrete,
    onChange,
  } = props;

  const handleCurrencyChange = React.useCallback(
    (event, value) => {
      const {
        target: { name },
      } = event;

      if (onChange) {
        onChange({
          identifiant,
          [name]: value,
        });
      }
    },
    [identifiant, onChange],
  );

  const handleDropdownChange = React.useCallback(
    (e, { name, value }) => {
      if (onChange) {
        onChange({
          identifiant,
          [name]: value,
        });
      }
    },
    [identifiant, onChange],
  );

  const periodiciteVersementOptions = React.useMemo(
    () =>
      _.map(PERIODICITE_VERSEMENT, (text, value) => ({
        key: value,
        value: text,
        text: text === 'Mensuel' ? 'Mensuelle' : text,
      })),
    [],
  );

  const periodiciteArreteOptions = React.useMemo(
    () =>
      _.map(PERIODICITE_ARRETE, (text, value) => ({
        key: value,
        value: text,
        text: text === 'Mensuel' ? 'Mensuelle' : text,
      })),
    [],
  );

  const circleInfo = React.useMemo(
    () => <Icon name="Info" iconSet="Lucide" size={20} style={{ marginLeft: 5 }} />,
    [],
  );

  const calculerProchaineDate = React.useCallback((periodicite: string, dateFin?: string) => {
    if (dateFin) {
      const base = dayjs(dateFin);
      const today = dayjs();

      // Si l'arrêté de novembre est traité en décembre, plafonner au 31 décembre
      const capDecembre31 = (date: string): string => {
        if (base.month() === 10 && base.year() === today.year() && today.month() === 11) {
          const dec31 = today.endOf('year');
          if (dayjs(date, 'DD/MM/YYYY').isAfter(dec31)) {
            return dec31.format('DD/MM/YYYY');
          }
        }
        return date;
      };

      switch (periodicite) {
        case 'Mensuel':
          return capDecembre31(base.add(30, 'day').format('DD/MM/YYYY'));
        case 'Bimensuel':
          return capDecembre31(base.add(15, 'day').format('DD/MM/YYYY'));
        case 'Hebdomadaire':
          return capDecembre31(base.add(7, 'day').format('DD/MM/YYYY'));
        default:
          return '';
      }
    }

    const today = dayjs();

    switch (periodicite) {
      case 'Mensuel':
        // Fin du mois en cours
        return today.endOf('month').format('DD/MM/YYYY');

      case 'Bimensuel':
        // Si avant le 15, prochaine date = 15 du mois
        // Sinon, prochaine date = fin du mois
        if (today.date() < 15) {
          return today.date(15).format('DD/MM/YYYY');
        }
        return today.endOf('month').format('DD/MM/YYYY');

      case 'Hebdomadaire':
        // Fin de la semaine (dimanche)
        return today.endOf('week').format('DD/MM/YYYY');

      default:
        return '';
    }
  }, []);

  const prochaineeDateVersement = React.useMemo(
    () =>
      calculerProchaineDate(periodiciteVersementEncaisseGlobale || 'Mensuel', dateFinDernierArrete),
    [calculerProchaineDate, periodiciteVersementEncaisseGlobale, dateFinDernierArrete],
  );

  const prochaineDateArrete = React.useMemo(
    () => calculerProchaineDate(periodiciteArrete || 'Mensuel', dateFinDernierArrete),
    [calculerProchaineDate, periodiciteArrete, dateFinDernierArrete],
  );

  return (
    <Grid stackable relaxed as={Form}>
      <Grid.Row style={{ paddingTop: 0 }}>
        {/* Section 1: Plafonds d'encaisse */}
        <Grid.Column width={4} style={{ paddingLeft: 0 }}>
          <SectionItem title="Plafonds d'encaisse" single compact>
            <Form.Field
              // @ts-expect-error
              id="encaisseMaximum"
              error={_.some(errors, { id: 'encaisseMaximum' })}
            >
              <Currency
                required
                data-testid="encaissement-maximum"
                value={encaisseMaximum}
                name="encaisseMaximum"
                label="Encaisse globale"
                placeholder=""
                description=""
                style={{ width: 125 }}
                valueMin={0}
                onBlur={handleCurrencyChange}
                devise
              />
            </Form.Field>
            <Form.Field
              // @ts-expect-error
              id="encaisseNumeraireMaximum"
              error={_.some(errors, { id: 'encaisseNumeraireMaximum' })}
            >
              <Currency
                data-testid="encaisse-numeraire-maximum"
                value={encaisseNumeraireMaximum}
                name="encaisseNumeraireMaximum"
                label="Encaisse en numéraire"
                placeholder=""
                description=""
                style={{ width: 125 }}
                valueMin={0}
                onBlur={handleCurrencyChange}
                devise
              />
            </Form.Field>
            <Form.Field
              // @ts-expect-error
              id="fondCaisse"
              error={_.some(errors, { id: 'fondCaisse' })}
            >
              <label style={{ display: 'flex', alignItems: 'center' }} title="">
                Fonds de caisse
                <Tooltip
                  type="warning"
                  trigger={circleInfo}
                  message={
                    <div style={{ width: 400 }}>
                      Le fonds de caisse n&apos;est pas comptabilisé dans l&apos;encaisse.
                    </div>
                  }
                  position="bottom center"
                />
              </label>
              <Currency
                data-testid="encaisse-fond-caisse"
                name="fondCaisse"
                value={fondCaisse}
                placeholder=""
                description=""
                style={{ width: 125 }}
                valueMin={0}
                onBlur={handleCurrencyChange}
                devise
              />
            </Form.Field>
          </SectionItem>
        </Grid.Column>

        {/* Section 2: Versements de l'encaisse */}
        <Grid.Column width={4}>
          <SectionItem title="Versements de l'encaisse" single compact>
            <Form.Field
              // @ts-expect-error
              style={{ marginBottom: 0 }}
            >
              <label style={{ display: 'flex', alignItems: 'center' }}>
                Périodicité des versements
                <Tooltip
                  type="warning"
                  trigger={circleInfo}
                  message={
                    <div style={{ width: 500 }}>
                      L&apos;encaisse est remise directement par le régisseur à la caisse du
                      comptable assignataire dès que le montant de l&apos;encaisse prévu par
                      l&apos;acte constitutif de la régie est atteint et au minimum une fois par
                      mois.
                    </div>
                  }
                  position="bottom center"
                />
              </label>
              <Dropdown
                data-testid="periodicite-versement"
                selection
                disabled
                name="periodiciteVersementEncaisseGlobale"
                value={periodiciteVersementEncaisseGlobale || 'Mensuel'}
                options={periodiciteVersementOptions}
                onChange={handleDropdownChange}
              />
            </Form.Field>
            {prochaineeDateVersement && (
              <Header disabled as="h6" style={{ marginTop: 5, float: 'right' }}>
                Prochain versement le <strong>{prochaineeDateVersement}</strong>
              </Header>
            )}
          </SectionItem>
        </Grid.Column>
        <Grid.Column width={1} />
        {/* Section 3: Arrêtés comptables */}
        <Grid.Column width={4} style={{ paddingRight: 0 }}>
          <SectionItem title="Arrêtés comptables" single compact>
            <Form.Field
              // @ts-expect-error
              style={{ marginBottom: 0 }}
            >
              <label style={{ display: 'flex', alignItems: 'center' }}>
                Périodicité des arrêtés
                <Tooltip
                  type="warning"
                  trigger={circleInfo}
                  message={
                    <div style={{ width: 500 }}>
                      Le régisseur doit verser les justifications des recettes qu&apos;il a
                      encaissées à la caisse du comptable selon la périodicité prévue par
                      l&apos;acte de création de la régie et au minimum une fois par mois.
                      <br />
                      <br />
                      La périodicité de versement des justifications de recettes peut être
                      différente de la périodicité de versement de l&apos;encaisse.
                    </div>
                  }
                  position="bottom center"
                />
              </label>
              <Dropdown
                data-testid="periodicite-arrete"
                selection
                disabled
                name="periodiciteArrete"
                value={periodiciteArrete || 'Mensuel'}
                options={periodiciteArreteOptions}
                onChange={handleDropdownChange}
              />
            </Form.Field>
            {prochaineDateArrete && (
              <Header disabled as="h6" style={{ marginTop: 5, float: 'right' }}>
                Prochain arrêté le <strong>{prochaineDateArrete}</strong>
              </Header>
            )}
          </SectionItem>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default EncaisseArretes;
